// Resolve a deep-linked meeting id to the top-level schedule row that holds
// it, for the cross-link page-jump (see ../shared/useDeepLinkPageJump).
// groupMeetings returns only parent/one-off rows at the top level; occurrences
// exist solely as `children` nested in their parent row, so a child match
// resolves to its PARENT's index with `expandParentId` set (the caller expands
// that parent to reveal the child). `target` carries the matched meeting
// itself (the CHILD for a nested match) — non-null exactly when index >= 0 —
// so the caller can open its view detail expand without a second lookup.

import type { Meeting } from "../../../../types";
import type { Row } from "./groupMeetings";

export interface MeetingDeepLinkTarget {
  /** 0-based index of the top-level row in the paginated list, or -1. */
  index: number;
  /** Parent schedule id to expand when the match was a nested child. */
  expandParentId: string | null;
  /** The matched meeting itself (the CHILD for a nested match), or null. */
  target: Meeting | null;
}

export function findMeetingDeepLinkTarget(
  itemId: string | null | undefined,
  rows: readonly Row[],
): MeetingDeepLinkTarget {
  if (!itemId) return { index: -1, expandParentId: null, target: null };
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.meeting.id === itemId)
      return { index: i, expandParentId: null, target: row.meeting };
    if (row.kind === "parent") {
      const child = row.children.find((c) => c.meeting.id === itemId);
      if (child)
        return {
          index: i,
          expandParentId: row.meeting.id,
          target: child.meeting,
        };
    }
  }
  return { index: -1, expandParentId: null, target: null };
}
